import type { BackendId, TrackingBackend } from "./types";

export interface TrackingManagerConfig {
  /** Backend ids to try, in order. The first supported one wins. */
  prefer: BackendId[];
}

/**
 * Negotiates the best tracking backend for the current device. Depends only on the abstract
 * backend contract (DIP), so it is tested with fakes and never imports a concrete engine.
 */
export class TrackingManager<Ctx> {
  constructor(private readonly config: TrackingManagerConfig) {}

  /** Supported candidates, in preference order. The single source of negotiation. */
  async orderedSupported(candidates: TrackingBackend<Ctx>[]): Promise<TrackingBackend<Ctx>[]> {
    const supported = new Set<BackendId>();
    await Promise.all(
      candidates.map(async (candidate) => {
        if (await candidate.isSupported()) supported.add(candidate.id);
      }),
    );

    return this.config.prefer
      .map((id) => candidates.find((candidate) => candidate.id === id && supported.has(id)))
      .filter((candidate): candidate is TrackingBackend<Ctx> => candidate !== undefined);
  }

  /** First candidate, in preference order, whose `isSupported()` resolves true. */
  async select(candidates: TrackingBackend<Ctx>[]): Promise<TrackingBackend<Ctx> | null> {
    return (await this.orderedSupported(candidates))[0] ?? null;
  }
}
